import type * as React from "react";

import { cn } from "../lib/cn";

/** Which highlighter came out of the drawer. */
export type SwipeTone = "working" | "flag" | "stopped" | "brian";

const toneWash: Record<SwipeTone, string> = {
  working: "bg-wash-working",
  flag: "bg-wash-flag",
  stopped: "bg-wash-stopped",
  brian: "bg-wash-brian",
};

/*
 * Icon + word geometry, exported so StatusMark (the unmarked half of the same
 * family) lays its rows out identically: a marked state and an unmarked one
 * standing side by side must agree on gap and icon alignment. Not in the
 * barrel - it is a shared recipe, not a component.
 */
export const markShape = {
  /** The row a swipe becomes once it carries an icon. */
  row: "inline-flex items-center gap-1",
  /** The icon slot: never squeezed, never on its own line. */
  icon: "inline-flex shrink-0 items-center",
} as const;

export interface SwipeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** working = in progress, flag = needs a look, stopped = halted, brian = Brian did this. */
  tone: SwipeTone;
  /**
   * Decorative glyph set before the word. Hidden from AT - the word already
   * says the state, so an announced icon would say it twice. Giving one turns
   * the swipe into a single-line row, so it no longer breaks across lines.
   */
  icon?: React.ReactNode;
  /** The word being marked. Required: a wash with no word is not a swipe. */
  children: React.ReactNode;
  ref?: React.Ref<HTMLSpanElement>;
}

/**
 * A highlighter stroke over a word: translucent marker ink, irregular ends,
 * dark text still legible underneath. This is how every status is expressed in
 * the product - a task, a room, a sync, a recording all say their state in a
 * word and wear the marker over it.
 *
 * The wash rule: a wash never appears without its word. There is no bare dot,
 * no empty swatch, no color-only badge. If you only have a color and no word
 * to say, use the matching `ink-*` (text, icon, border) instead.
 *
 * Font and size are inherited so a swipe can mark a heading, a table cell or a
 * line of body copy without being resized.
 */
export function Swipe({
  tone,
  icon,
  children,
  className,
  ...rest
}: SwipeProps) {
  return (
    <span
      className={cn(
        toneWash[tone],
        "blend-marker rounded-swipe text-ink",
        // padding: .1em .45em .15em - the atlas stroke, sized in the inherited font
        "px-[0.45em] pt-[0.1em] pb-[0.15em]",
        // a swipe broken across lines reads as two separate strokes, not one
        // stretched box with clipped ends
        "[box-decoration-break:clone] [-webkit-box-decoration-break:clone]",
        // An icon needs a row to sit in; without one the swipe stays inline so
        // it can still wrap mid-phrase.
        icon != null && markShape.row,
        className,
      )}
      {...rest}
    >
      {icon != null && (
        <span aria-hidden="true" className={markShape.icon}>
          {icon}
        </span>
      )}
      {children}
    </span>
  );
}
